import {
	AllCommunityModule,
	ClientSideRowModelModule,
	type ColDef,
	type ColGroupDef,
	type FilterChangedEvent,
	type GetRowIdFunc,
	type GridReadyEvent,
	type HeaderClassParams,
	type IDatasource,
	InfiniteRowModelModule,
	ModuleRegistry,
	type PaginationChangedEvent,
	type RowClassParams,
	type RowClickedEvent,
	type RowStyle,
	type SortChangedEvent,
	type ThemeDefaultParams,
	themeQuartz,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { forwardRef, type ReactNode, type Ref, useMemo } from "react";
import {
	DEFAULT_CACHE_BLOCK_SIZE,
	DEFAULT_PAGE_SIZE,
	DEFAULT_PAGE_SIZE_SELECTOR,
} from "@/constants";
// Uncomment when the i18N is properly set up and translation text is ready
// import { useTranslation } from "react-i18next";
// import { getAgGridLocaleText } from "./AgGridLocaleText";
import CostcoBlueLoader from "../../LoadingIndicator";

type DefaultGridProps<T> = {
	rowId: GetRowIdFunc<T>;
	datasource?: IDatasource;
	columnDefs: (ColDef<T> | ColGroupDef<T>)[];
	rowData?: T[];
	pinnedTopRowData?: T[];
	loading?: boolean;
	onSortChanged?: (event: SortChangedEvent<T>) => void;
	onFilterChanged?: (event: FilterChangedEvent<T>) => void;
	onRowClicked?: (event: RowClickedEvent<T>) => void;
	onGridReady?: (event: GridReadyEvent<T>) => void;
	onPaginationPageSizeChanged?: (event: PaginationChangedEvent) => void;
	noRowsFoundText?: string;
	quickFilterText?: string;
	multiSortKey?: "ctrl" | "shift";
	domLayout?: "autoHeight" | "normal";
	getRowStyle?: (params: RowClassParams<T>) => RowStyle | undefined;
	// Infinite row model specific props
	rowModelType?: "clientSide" | "infinite";
	cacheBlockSize?: number;
	maxBlocksInCache?: number;
	context?: Record<string, unknown>;
	masterDetail?: boolean;
	detailRowAutoHeight?: boolean;
	isRowMaster?: (data: T) => boolean;
	detailCellRenderer?: string | React.ComponentType<unknown>;
	pagination?: boolean;
};

ModuleRegistry.registerModules([
	InfiniteRowModelModule,
	AllCommunityModule,
	ClientSideRowModelModule,
]);

const gridTheme: Partial<ThemeDefaultParams> = {
	fontSize: 14,
	spacing: 10,
	rowHeight: 42,
	oddRowBackgroundColor: "#FAFAFB",
	headerRowBorder: { width: 2 },
	headerFontWeight: 600,
	wrapperBorder: false,
	rowBorder: { style: "solid" },
	rowHoverColor: "rgba(245,245,245,0.5)",
	columnHoverColor: "rgba(238,238,238,0.4)",
	columnBorder: { style: "solid" },
};

// Standard text/number filters. Excludes custom filters (e.g., YearFilter) and unfiltered columns.
const STANDARD_FILTER_VALUES: unknown[] = [true, "agNumberColumnFilter"];

// Marker class for standard filter headers. Paired with native `.ag-header-cell-filtered`
// in index.css to restore active filter indicator styles lost in the theme migration.
export const FILTER_ACTIVE_HEADER_CLASS = "ag-header-cell--filterable";

// Exported for testing: merges the marker class into the base header class if filter is standard.
export const resolveHeaderClass = (params: HeaderClassParams): string[] => {
	const filter = (params.colDef as ColDef).filter;
	const isStandardFilterColumn = STANDARD_FILTER_VALUES.includes(filter as never);
	return isStandardFilterColumn
		? ["defaultTableHeader", FILTER_ACTIVE_HEADER_CLASS]
		: ["defaultTableHeader"];
};

/**
 * Group-aware comparator:
 * - Cross-group: Sorts by cell value.
 * - Same-group: Pins history rows below parent in API order (historyIndex),
 *   canceling AG Grid's DESC negation to maintain fixed relative positioning.
 */
export const groupAwareComparator = (
	valueA: any,
	valueB: any,
	nodeA: any,
	nodeB: any,
	isDescending?: boolean,
): number => {
	const dataA = nodeA?.data;
	const dataB = nodeB?.data;
	const idxA = dataA?.__parentIndex ?? nodeA?.rowIndex ?? 0;
	const idxB = dataB?.__parentIndex ?? nodeB?.rowIndex ?? 0;
	if (idxA !== idxB) return idxA - idxB;

	// Same parent group: keep the latest row first, followed by history rows in the exact
	// order the API returned them (historyIndex) — regardless of column values or sort
	// direction. AG Grid negates our result for "desc" sorts, so negate here first to
	// cancel that out and keep this relative order fixed for both asc and desc.
	if (dataA?.rowType === "history" || dataB?.rowType === "history") {
		const rankA = dataA?.rowType === "history" ? 1 + (dataA.historyIndex ?? 0) : 0;
		const rankB = dataB?.rowType === "history" ? 1 + (dataB.historyIndex ?? 0) : 0;
		const diff = rankA - rankB;
		return isDescending ? -diff : diff;
	}

	// Same parent group — perform default comparison for numbers/strings
	if (valueA == null && valueB == null) return 0;
	if (valueA == null) return -1;
	if (valueB == null) return 1;

	if (typeof valueA === "number" && typeof valueB === "number") return valueA - valueB;

	const aStr = String(valueA).toLowerCase();
	const bStr = String(valueB).toLowerCase();
	if (aStr < bStr) return -1;
	if (aStr > bStr) return 1;
	return 0;
};

const defsStyle: Partial<ColDef> = {
	headerClass: resolveHeaderClass,
	flex: 1,
	filterParams: { maxNumConditions: 1 },
	resizable: true, // Enable resizing
	autoHeight: true, // Adjust Cell Height to Fit Wrapped Text
	autoHeaderHeight: true, // Adjust Header Height to Fit Wrapped Text
	sortingOrder: ["asc", "desc", null], // Set sorting order to cycle through ascending, descending, and no sort
	// Use comparator that preserves parent/history grouping when grid performs client-side sorting
	comparator: groupAwareComparator as unknown as any,
};

const DefaultGridInner = <T,>(
	{
		columnDefs,
		rowData,
		datasource,
		pinnedTopRowData,
		loading,
		onSortChanged,
		onFilterChanged,
		onRowClicked,
		onPaginationPageSizeChanged,
		rowId,
		noRowsFoundText,
		onGridReady,
		quickFilterText,
		multiSortKey,
		getRowStyle,
		domLayout = "autoHeight",
		rowModelType,
		cacheBlockSize,
		maxBlocksInCache,
		context,
		masterDetail,
		detailRowAutoHeight,
		isRowMaster,
		detailCellRenderer,
		pagination, // <-- ADDED THIS: Destructured pagination here for clarity, but not used in the component. It can be passed down to AgGridReact if needed.
	}: DefaultGridProps<T>,
	ref: Ref<AgGridReact<T>>,
): ReactNode => {
	// const { t } = useTranslation();
	const loadingOverlay = () => <CostcoBlueLoader />;

	// Determine model type: use explicit prop if provided, otherwise infer from datasource
	const modelType = rowModelType ?? (datasource ? "infinite" : "clientSide");

	// Use explicit cacheBlockSize if provided, otherwise use default for datasource
	const blockSize = cacheBlockSize ?? (datasource ? DEFAULT_CACHE_BLOCK_SIZE : undefined);

	const quickFilter = !datasource && quickFilterText ? quickFilterText : undefined;

	// History rows only carry the ~7 historical fields returned by the history API;
	const defaultColDef = useMemo<ColDef>(() => {
		return {
			...defsStyle,
			valueGetter: (params: any) => {
				const data = params?.data as any;
				const field = params?.colDef?.field as string | undefined;
				// Preserve existing value if present, otherwise fall back to reading from data
				if (params && typeof params.value !== "undefined") return params.value;
				return field ? data?.[field] : undefined;
			},
		};
	}, []);

	const rowClassName = onRowClicked ? "cursor-pointer" : "";

	return (
		<AgGridReact<T>
			getRowId={rowId}
			rowModelType={modelType}
			rowData={rowData}
			datasource={datasource ?? undefined}
			domLayout={domLayout}
			animateRows={false}
			columnDefs={columnDefs}
			cacheBlockSize={blockSize}
			maxBlocksInCache={maxBlocksInCache}
			infiniteInitialRowCount={DEFAULT_PAGE_SIZE}
			defaultCsvExportParams={{}}
			defaultColDef={defaultColDef}
			loading={loading}
			// localeText={getAgGridLocaleText(t)}
			pagination={pagination ?? false} // Use the pagination prop if provided, otherwise default to false
			paginationPageSize={DEFAULT_PAGE_SIZE}
			paginationPageSizeSelector={DEFAULT_PAGE_SIZE_SELECTOR}
			onPaginationChanged={onPaginationPageSizeChanged}
			valueCache
			// rowSelection={{ mode: "singleRow", checkboxes: false }}
			multiSortKey={multiSortKey === "ctrl" ? "ctrl" : undefined}
			suppressMultiSort={!multiSortKey}
			onSortChanged={onSortChanged}
			onFilterChanged={onFilterChanged}
			onRowClicked={onRowClicked}
			columnHoverHighlight
			quickFilterText={quickFilter}
			tooltipMouseTrack
			rowClass={rowClassName}
			className="h-96"
			theme={themeQuartz.withParams(gridTheme)}
			loadingOverlayComponent={loadingOverlay}
			overlayNoRowsTemplate={noRowsFoundText}
			onGridReady={onGridReady}
			pinnedTopRowData={pinnedTopRowData}
			context={context}
			getRowStyle={(params) => {
				const data = params.data as { isHistoryRow?: boolean } | undefined;
				if (data?.isHistoryRow) {
					return { backgroundColor: "#F8FAFC", fontStyle: "italic" };
				}
				return getRowStyle?.(params);
			}}
			masterDetail={masterDetail}
			detailRowAutoHeight={detailRowAutoHeight}
			isRowMaster={isRowMaster}
			detailCellRenderer={detailCellRenderer}
			ref={ref}
		/>
	);
};

// Wrap the component with forwardRef
const DefaultGrid = forwardRef(DefaultGridInner) as <T>(
	props: DefaultGridProps<T> & { ref?: Ref<AgGridReact<T>> },
) => ReactNode;

export default DefaultGrid;
